import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import StarIcon from "@mui/icons-material/Star";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WebOutlinedIcon from "@mui/icons-material/WebOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import posthog from "../../posthog";
import cinchLogo from "../../assets/cinch-logo.svg";
import { maskEmail, getEmailProviderInbox } from "./signup-email";

const CINCH_BLUE = "#0875BF";
const CINCH_BLUE_DARK = "#055185";
const CINCH_BLUE_LIGHT = "#0994F2";
const CINCH_BLUE_200 = "#BFE4FC";
const CINCH_BLUE_400 = "#44AFF8";
const TEXT_PRIMARY = "#0B0C0E";
const TEXT_SECONDARY = "rgba(1,12,20,0.6)";
const BORDER_LIGHT = "rgba(1,12,20,0.12)";
const APP_BG = "#F4F4F6";
const TRIAL_ORANGE = "#E26008"; // light/orange/500 — setup-link card border
const WARNING_ORANGE = "#C05107"; // border/warning — FREE TIER chip
const GOOGLE_BLUE = "#2563EB";
const GOOGLE_GREEN = "#0A8039";
const GOOGLE_BORDER = "#D5D7DD";
const HERO_GRADIENT = `linear-gradient(180deg, ${CINCH_BLUE_LIGHT} 25%, ${CINCH_BLUE_DARK})`;
const FREE_TIER_GRADIENT = `linear-gradient(180deg, ${CINCH_BLUE} 0%, #033354 100%)`;

const SCALE_CONTACT_MAILTO = `mailto:support@cinch.io?subject=${encodeURIComponent("Scheduler — interested in the Scale plan")}`;

const HERO_STEPS = [
  { step: 1, title: "Set your password" },
  { step: 2, title: "Add your locations and hours" },
  { step: 3, title: "Include services and pricing" },
];

const FEATURE_CARDS = [
  {
    Icon: EditOutlinedIcon,
    title: "Setup Starts Filled In",
    description: "We pull your hours, services, and prices straight from your website, so your calendar is built before you touch it.",
  },
  {
    Icon: WebOutlinedIcon,
    title: "Edit Anything in Seconds",
    description: "Every detail we bring in lands as an editable field. Update a price, add a service, or adjust hours with a click.",
  },
  {
    Icon: EventAvailableOutlinedIcon,
    title: "Live in Under 5 Minutes",
    description: "Review what we found, make a test booking, and publish. Share your booking link the same day you sign up.",
  },
];

const FREE_TIER_FEATURES = [
  "“Book Online” button on your Google listing",
  "Booking widget on your website",
  "Email confirmations and reminders",
  "Calendar, customer list, service history",
  "Self-serve rescheduling and cancellations",
  "Your hours, your branding",
  "Unlimited locations",
  "Unlimited appointments",
];

const SCALE_FEATURES = [
  "Text confirmations, reminders, and two-way texting",
  "Customer profiles synced with your business software",
  "Automated campaigns that bring customers back",
  "Revenue by service, location, and campaign",
];

const FAQS = [
  {
    q: "Is Scheduler really free?",
    a: "Yes. Scheduler is free, with unlimited locations and unlimited appointments.",
  },
  {
    q: "How long does setup take?",
    a: "Under 5 minutes. Set your password, add your locations and services, and you’re ready to book.",
  },
  {
    q: "Do I need AI Voice to use Scheduler?",
    a: "Scheduler works on its own, so your team can start booking right away. If you run AI Voice, connect it and your agent books on the call, too.",
  },
  {
    q: "Can I set different hours for each location?",
    a: "Yes. Every location gets its own hours and services.",
  },
];

// maxWidth 1312 + 32px padding → ~1248px content, matching the 1440 design's
// 96px side gutters. px stays 16 on mobile to match the design's 16px gutter.
const CONTAINER_SX = { maxWidth: 1312, mx: "auto", px: { xs: 2, sm: 4 }, width: "100%", boxSizing: "border-box" };

const Band = ({ bg, children }) => (
  <Box sx={{ bgcolor: bg }}>
    <Box sx={{ ...CONTAINER_SX, py: { xs: 4, sm: 5 } }}>{children}</Box>
  </Box>
);

const SectionHeading = ({ title, subtitle }) => (
  <Stack spacing={0.25} sx={{ mb: 3 }}>
    <Typography sx={{ fontWeight: 700, fontSize: "20px", color: TEXT_PRIMARY, lineHeight: "28px" }}>{title}</Typography>
    {subtitle ? <Typography sx={{ fontWeight: 400, fontSize: "16px", color: TEXT_SECONDARY, lineHeight: "28px", letterSpacing: "0.15px" }}>{subtitle}</Typography> : null}
  </Stack>
);

const FeatureCard = ({ Icon, title, description }) => (
  <Paper elevation={0} sx={{ flex: 1, minWidth: 0, p: 3, borderRadius: "16px", bgcolor: "rgba(255,255,255,0.5)", border: `1px solid ${BORDER_LIGHT}` }}>
    <Stack spacing={2}>
      <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: CINCH_BLUE_200, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon sx={{ fontSize: 20, color: CINCH_BLUE_DARK }} />
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: "16px", color: TEXT_PRIMARY, lineHeight: "24px", letterSpacing: "0.25px" }}>{title}</Typography>
      <Typography sx={{ fontWeight: 500, fontSize: "16px", color: TEXT_SECONDARY, lineHeight: "24px", letterSpacing: "0.15px" }}>{description}</Typography>
    </Stack>
  </Paper>
);

// Illustrative Google Business Profile card — shows where the "Book Online"
// button lands once the calendar is live (Reserve with Google). Static mock.
const GoogleListingCard = () => {
  const pillSx = {
    "px": 2,
    "py": 0.75,
    "borderRadius": "100px",
    "fontWeight": 600,
    "fontSize": "13px",
    "lineHeight": "20px",
    "textTransform": "none",
    "whiteSpace": "nowrap",
    "minWidth": 0,
  };
  return (
    <Paper
      elevation={0}
      aria-hidden
      sx={{ width: "100%", maxWidth: 420, minWidth: 0, boxSizing: "border-box", bgcolor: "#fff", borderRadius: "16px", p: 2.5, border: `1px solid ${GOOGLE_BORDER}`, boxShadow: "2px 2px 12px 0px rgba(0,0,0,0.12)" }}
    >
      <Stack spacing={1.25}>
        <Typography sx={{ fontWeight: 700, fontSize: "17px", color: TEXT_PRIMARY, lineHeight: "24px" }}>Helpful Auto Shop</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 500, fontSize: "13px", color: TEXT_PRIMARY, lineHeight: "18px" }}>4.7</Typography>
          <Stack direction="row" alignItems="center">
            {[0, 1, 2, 3, 4].map((i) => (
              <StarIcon key={i} sx={{ fontSize: 13, color: "#F5A623" }} />
            ))}
          </Stack>
          <Typography sx={{ fontWeight: 400, fontSize: "13px", color: TEXT_SECONDARY, lineHeight: "18px" }}>
            (128) · Auto repair shop · 1230 N Freedom Blvd
          </Typography>
        </Stack>
        <Typography sx={{ fontWeight: 400, fontSize: "13px", lineHeight: "18px" }}>
          <Box component="span" sx={{ color: GOOGLE_GREEN, fontWeight: 600 }}>Open</Box>
          <Box component="span" sx={{ color: TEXT_SECONDARY }}> · Closes 6:00 PM</Box>
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
          <Box sx={{ ...pillSx, bgcolor: GOOGLE_BLUE, color: "#fff" }}>Book Online</Box>
          <Box sx={{ ...pillSx, bgcolor: "transparent", color: GOOGLE_BLUE, border: `1px solid ${GOOGLE_BORDER}` }}>Directions</Box>
          <Box sx={{ ...pillSx, bgcolor: "transparent", color: GOOGLE_BLUE, border: `1px solid ${GOOGLE_BORDER}` }}>Call</Box>
        </Stack>
      </Stack>
    </Paper>
  );
};

const FreeTierCard = () => (
  <Paper elevation={0} sx={{ flex: 1, minWidth: 0, borderRadius: "16px", p: 3, background: FREE_TIER_GRADIENT, boxShadow: "0px 1px 10px 0px rgba(0,0,0,0.12), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 2px 4px 0px rgba(0,0,0,0.2)" }}>
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography sx={{ fontWeight: 700, fontSize: "20px", color: "#fff", lineHeight: "28px" }}>Free Tier</Typography>
        <Typography sx={{ fontWeight: 400, fontSize: "16px", color: "rgba(255,255,255,0.85)", lineHeight: "24px", letterSpacing: "0.15px" }}>
          Everything you need to start booking customers.
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="baseline">
        <Typography sx={{ fontWeight: 700, fontSize: "32px", color: "#fff", lineHeight: "40px" }}>$0</Typography>
        <Typography sx={{ fontWeight: 400, fontSize: "16px", color: "rgba(255,255,255,0.85)", lineHeight: "24px" }}>/ month</Typography>
      </Stack>
      <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "rgba(255,255,255,0.85)", lineHeight: "22px", letterSpacing: "0.1px" }}>Includes:</Typography>
      <Stack spacing={1.25}>
        {FREE_TIER_FEATURES.map((feat) => (
          <Stack key={feat} direction="row" spacing={1.25} alignItems="flex-start">
            <CheckCircleOutlineIcon sx={{ fontSize: 20, color: CINCH_BLUE_200, flexShrink: 0, mt: "2px" }} />
            <Typography sx={{ fontWeight: 500, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px" }}>{feat}</Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  </Paper>
);

const ScalePlanCard = () => (
  <Paper elevation={0} sx={{ display: "flex", flex: 1, minWidth: 0, borderRadius: "16px", p: 3, bgcolor: "#fff", border: `1px solid ${BORDER_LIGHT}` }}>
    <Stack spacing={2} useFlexGap sx={{ width: "100%", height: "100%" }}>
      <Stack spacing={0.5}>
        <Typography sx={{ fontWeight: 700, fontSize: "20px", color: TEXT_PRIMARY, lineHeight: "28px" }}>Scale Plan</Typography>
        <Typography sx={{ fontWeight: 400, fontSize: "16px", color: TEXT_SECONDARY, lineHeight: "24px", letterSpacing: "0.15px" }}>
          Best for businesses ready to grow revenue.
        </Typography>
      </Stack>
      <Typography sx={{ fontWeight: 700, fontSize: "32px", color: TEXT_PRIMARY, lineHeight: "40px" }}>Custom</Typography>
      <Typography sx={{ fontWeight: 600, fontSize: "14px", color: TEXT_SECONDARY, lineHeight: "22px", letterSpacing: "0.1px" }}>Everything in the Free Tier plus:</Typography>
      <Stack spacing={1.25}>
        {SCALE_FEATURES.map((feat) => (
          <Stack key={feat} direction="row" spacing={1.25} alignItems="flex-start">
            <AddOutlinedIcon sx={{ fontSize: 20, color: CINCH_BLUE, flexShrink: 0, mt: "2px" }} />
            <Typography sx={{ fontWeight: 500, fontSize: "16px", color: TEXT_PRIMARY, lineHeight: "24px", letterSpacing: "0.15px" }}>{feat}</Typography>
          </Stack>
        ))}
      </Stack>
      <Button
        fullWidth
        variant="contained"
        href={SCALE_CONTACT_MAILTO}
        sx={{
          "mt": "auto",
          "bgcolor": CINCH_BLUE,
          "color": "#fff",
          "fontWeight": 600,
          "fontSize": "16px",
          "textTransform": "none",
          "borderRadius": "8px",
          "py": 1,
          "minHeight": 40,
          "boxShadow": "none",
          "&:hover": { bgcolor: CINCH_BLUE_DARK, boxShadow: "none" },
        }}
      >
        Contact Us
      </Button>
    </Stack>
  </Paper>
);

/**
 * Scheduler signup confirmation page (CIN-6930). Shown to every prospect who
 * submits the Scheduler PLG form — identical regardless of whether the email is
 * new or already has an account, so the page can't enumerate Cinch users. The
 * masked address is the user's own submitted value. The blue header is sticky
 * and collapses to a compact bar on scroll, matching the AI Voice page.
 */
export default function SchedulerSuccess({ email, onStartOver, featureFocus }) {
  const maskedEmail = maskEmail(email);
  const inboxUrl = getEmailProviderInbox(email);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    document.title = "Let’s Activate Your Account — Cinch";
    posthog.capture("signup_success_viewed", { feature_focus: featureFocus });
  }, [featureFocus]);

  // Show the compact bar once the hero has (almost) scrolled off. The hero never
  // resizes — it just scrolls away and a slim fixed bar slides in over it — so
  // there's no large reflow, the motion is smooth, and a plain scroll threshold
  // is stable (no anchoring/oscillation to fight).
  useEffect(() => {
    const onScroll = () => {
      const heroHeight = heroRef.current ? heroRef.current.offsetHeight : 300;
      setCollapsed(window.scrollY > heroHeight - 72);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const emailChip = maskedEmail ? (
    <Box sx={{ bgcolor: "rgba(255,255,255,0.1)", border: `1px solid ${CINCH_BLUE_400}`, borderRadius: "8px", px: 1.5, py: 0.5, display: "inline-flex" }}>
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px" }}>{maskedEmail}</Typography>
    </Box>
  ) : null;

  const sendItAgain = onStartOver ? (
    <>
      , or{" "}
      <Link
        component="button"
        type="button"
        onClick={onStartOver}
        underline="always"
        sx={{ color: "#fff", textDecorationColor: "#fff", font: "inherit", fontWeight: 400, verticalAlign: "baseline", p: 0, border: 0, background: "none", cursor: "pointer" }}
      >
        send it again
      </Link>
    </>
  ) : null;

  const openMyEmailButton = inboxUrl ? (
    <Button
      variant="contained"
      href={inboxUrl}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<MailOutlineIcon sx={{ fontSize: "20px !important" }} />}
      sx={{
        "bgcolor": "#fff",
        "color": CINCH_BLUE,
        "fontWeight": 600,
        "fontSize": "16px",
        "textTransform": "none",
        "borderRadius": "8px",
        "px": 4,
        "py": 1,
        "minHeight": 40,
        "flexShrink": 0,
        "boxShadow": "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)",
        "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
      }}
    >
      Open My Email
    </Button>
  ) : null;

  const openMyEmailBlueButton = inboxUrl ? (
    <Button
      variant="contained"
      href={inboxUrl}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<MailOutlineIcon sx={{ fontSize: "20px !important" }} />}
      sx={{
        "bgcolor": CINCH_BLUE,
        "color": "#fff",
        "fontWeight": 600,
        "fontSize": "16px",
        "textTransform": "none",
        "borderRadius": "8px",
        "px": 4,
        "py": 1,
        "minHeight": 40,
        "boxShadow": "none",
        "&:hover": { bgcolor: CINCH_BLUE_DARK, boxShadow: "none" },
      }}
    >
      Open My Email
    </Button>
  ) : null;

  const heroSteps = (
    <Stack spacing={1}>
      {HERO_STEPS.map(({ step, title }) => (
        <Stack key={step} direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: CINCH_BLUE_200, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "12px", color: TEXT_PRIMARY, lineHeight: "20px" }}>{step}</Typography>
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px", flex: 1 }}>{title}</Typography>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Box sx={{ position: "relative", minHeight: "100vh", bgcolor: "#fff", boxSizing: "border-box" }}>
        {/* Compact bar — fixed, slides down over the page once the hero has
            scrolled off. Nothing resizes, so the motion stays smooth. Mobile
            shows just the title + button; sm+ adds the email line. */}
        <Box
          aria-hidden={!collapsed}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            background: HERO_GRADIENT,
            transform: collapsed ? "translateY(0)" : "translateY(-100%)",
            transition: "transform 0.28s ease",
            boxShadow: collapsed ? "0 2px 10px rgba(0,0,0,0.18)" : "none",
            willChange: "transform",
          }}
        >
          <Box sx={{ ...CONTAINER_SX, py: { xs: 1.5, sm: 2 } }}>
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: "18px", sm: "24px" }, color: "#fff", lineHeight: "32px" }}>Let’s Activate Your Account</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ display: { xs: "none", sm: "flex" } }}>
                    <Typography sx={{ fontWeight: 400, fontSize: "14px", color: "rgba(255,255,255,0.85)", lineHeight: "22px" }}>We sent a setup link to</Typography>
                    {emailChip}
                  </Stack>
                </Stack>
                <Typography sx={{ fontWeight: 400, fontSize: "14px", color: "rgba(255,255,255,0.85)", lineHeight: "22px", display: { xs: "none", sm: "block" } }}>
                  Not there? Check spam{sendItAgain}
                </Typography>
              </Stack>
              {openMyEmailButton}
            </Stack>
          </Box>
        </Box>

        {/* Full hero — full-bleed blue; scrolls away naturally. Two columns on
            md, full-height with the step list pinned under the CTA on mobile. */}
        <Box ref={heroRef} sx={{ background: HERO_GRADIENT }}>
          <Box sx={{ ...CONTAINER_SX, py: { xs: 4, sm: 5 } }}>
            <Stack spacing={{ xs: 4, md: 3 }}>
              <Stack spacing={4}>
                <Box
                  sx={{
                    width: 96,
                    height: 32,
                    bgcolor: "#fff",
                    mask: `url(${cinchLogo}) no-repeat left center / contain`,
                    WebkitMask: `url(${cinchLogo}) no-repeat left center / contain`,
                  }}
                />
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ border: "1px solid #fff", borderRadius: "100px", px: 1, py: 0.5, display: "flex", alignItems: "center", minHeight: 24 }}>
                    <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#fff", lineHeight: "16px", letterSpacing: "0.16px" }}>NEW FEATURE</Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.25px" }}>Scheduler</Typography>
                </Stack>
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 4, md: 5 }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "flex-start" }}
              >
                <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "28px", color: "#fff", lineHeight: "36px" }}>Let’s Activate Your Account</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px" }}>We sent a setup link to</Typography>
                    {emailChip}
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px" }}>Open it to set your password and get started.</Typography>
                  </Stack>
                  {openMyEmailButton ? <Box>{openMyEmailButton}</Box> : null}
                  <Typography sx={{ fontWeight: 400, fontSize: "16px", color: "rgba(255,255,255,0.85)", lineHeight: "24px" }}>
                    Not there? Check spam{sendItAgain}
                  </Typography>
                </Stack>

                <Stack spacing={2} sx={{ flexShrink: 0 }}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px" }}>Setup Takes Just 5 Minutes</Typography>
                    <Typography sx={{ fontWeight: 400, fontSize: "16px", color: "rgba(255,255,255,0.85)", lineHeight: "24px" }}>Start capturing customer bookings today.</Typography>
                  </Stack>
                  {heroSteps}
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* Reserve with Google — white band */}
        <Band bg="#fff">
          <SectionHeading
            title="Get the “Book Online” Button on Your Google Listing"
            subtitle="Customers find you on Google and book on the spot, before they ever reach your website."
          />
          <Stack spacing={3} alignItems="flex-start">
            <GoogleListingCard />
            <Typography sx={{ fontWeight: 400, fontSize: "16px", color: TEXT_PRIMARY, lineHeight: "28px", letterSpacing: "0.15px" }}>
              Cinch is an approved Reserve with Google partner, so the button turns on as soon as your calendar is live. Cinch handles the Google setup
              for you. Bookings from Google, your website, and marketing all land on one calendar with one set of open slots.
            </Typography>
          </Stack>
        </Band>

        {/* Built from your website — grey band */}
        <Band bg={APP_BG}>
          <SectionHeading title="Your Calendar, Built From Your Website in Minutes." subtitle="We scan your site and fill in the details. You review, adjust, and start booking." />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {FEATURE_CARDS.map((card) => (
              <FeatureCard key={card.title} {...card} />
            ))}
          </Stack>
        </Band>

        {/* Free means free — white band, pricing cards */}
        <Band bg="#fff">
          <SectionHeading title="Free Means Free, Here’s What the Scale Plan Adds" subtitle="Ready to grow? Unlock the full Cinch platform to bring every customer back again and again." />
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="stretch">
            <FreeTierCard />
            <ScalePlanCard />
          </Stack>
        </Band>

        {/* Meet Cinch — grey band, setup-link card */}
        <Band bg={APP_BG}>
          <SectionHeading title="Meet Cinch, the Platform Behind Scheduler" />
          <Stack spacing={3}>
            <Typography sx={{ fontWeight: 400, fontSize: "16px", color: TEXT_PRIMARY, lineHeight: "28px", letterSpacing: "0.15px" }}>
              Cinch is the customer data and marketing platform local businesses use to keep their records straight and bring customers back. Scheduler
              is one piece of it, so every booking creates a customer record (who they are, their history, what they came in for) that keeps working
              long after the appointment.
            </Typography>
            <Paper elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${TRIAL_ORANGE}`, p: 3, bgcolor: "#fff", boxShadow: "0px 1px 10px 0px rgba(0,0,0,0.12), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 2px 4px 0px rgba(0,0,0,0.2)" }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column-reverse", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
                  <Typography sx={{ fontWeight: 700, fontSize: "16px", color: TEXT_PRIMARY, lineHeight: "24px", letterSpacing: "0.25px" }}>Your setup link is waiting in your inbox.</Typography>
                  <Box sx={{ border: `1px solid ${WARNING_ORANGE}`, borderRadius: "100px", px: 1.5, py: 0.5, flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 500, fontSize: "12px", color: WARNING_ORANGE, lineHeight: "16px", letterSpacing: "0.16px", whiteSpace: "nowrap" }}>FREE TIER</Typography>
                  </Box>
                </Stack>
                <Typography sx={{ fontWeight: 500, fontSize: "16px", color: TEXT_SECONDARY, lineHeight: "24px", letterSpacing: "0.15px" }}>
                  Get started quickly and set up a calendar for each business location. Unlimited locations are included for free. When you’re ready to
                  send text reminders, integrate your customer data, and get revenue reporting, Scale is there.
                </Typography>
                {openMyEmailBlueButton ? <Box>{openMyEmailBlueButton}</Box> : null}
              </Stack>
            </Paper>
          </Stack>
        </Band>

        {/* FAQ — white band, white card */}
        <Band bg="#fff">
          <Paper elevation={0} sx={{ bgcolor: "#fff", borderRadius: "16px", border: `1px solid ${BORDER_LIGHT}` }}>
            <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: `1px solid ${BORDER_LIGHT}` }}>
              <Typography sx={{ fontWeight: 700, fontSize: "20px", color: TEXT_PRIMARY, lineHeight: "28px" }}>Frequently Asked Questions</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: "16px", color: TEXT_SECONDARY, lineHeight: "28px", letterSpacing: "0.15px" }}>Get answers to common questions about Scheduler.</Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              {FAQS.map(({ q, a }, i) => (
                <Accordion
                  key={q}
                  elevation={0}
                  disableGutters
                  expanded={expandedFaq === i}
                  onChange={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  sx={{ "bgcolor": "#fff", "borderBottom": `1px solid ${BORDER_LIGHT}`, "&:before": { display: "none" }, "&.Mui-expanded": { mb: 0 } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: TEXT_PRIMARY }} />}
                    sx={{ "px": 2, "py": 1.5, "minHeight": "auto", "& .MuiAccordionSummary-content": { my: 0 } }}
                  >
                    <Typography sx={{ fontWeight: 500, fontSize: "16px", color: TEXT_PRIMARY, lineHeight: "24px", letterSpacing: "0.15px" }}>{q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                    <Typography sx={{ fontWeight: 400, fontSize: "14px", color: TEXT_PRIMARY, lineHeight: "22px" }}>{a}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Paper>
        </Band>
      </Box>
  );
}
